import React, { useState } from 'react'
import { GestureResponderEvent } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Popover, PopoverBackdrop, PopoverContent } from '../popover'
import { Pressable } from '../pressable'
import { MoreVerticalGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'

export type MoreMenuItem = {
    label: string
    onSelect(): void
    disabled?: boolean
    destructive?: boolean
}

export type MoreMenuProps = {
    items: MoreMenuItem[]
    disabled?: boolean
    placement?: 'bottom right' | 'bottom left' | 'top right' | 'top left'
    triggerClassName?: string
    contentClassName?: string
    testID?: string
    nativeID?: string
}

const stop = (e?: GestureResponderEvent) => {
    ;(e as unknown as { stopPropagation?: () => void })?.stopPropagation?.()
}

/**
 * Dark, tenant-agnostic replacement for the legacy `aos-ui` `More` popup: a
 * three-dot {@link MoreVerticalGlyph} trigger opening an anchored {@link Popover}
 * menu of actions on `bg/elevation` (mirrors {@link StatusDropdown}). Presentational
 * + controlled — the caller passes already-translated labels and handlers.
 */
export const MoreMenu: React.FC<MoreMenuProps> = ({
    items,
    disabled = false,
    placement = 'bottom right',
    triggerClassName,
    contentClassName,
    testID,
    nativeID,
}) => {
    const [isOpen, setIsOpen] = useState(false)

    if (!items.length) {
        return null
    }

    const select = (item: MoreMenuItem) => (e?: GestureResponderEvent) => {
        stop(e)
        if (item.disabled) {
            return
        }
        setIsOpen(false)
        item.onSelect()
    }

    return (
        <Popover
            isOpen={isOpen && !disabled}
            onClose={() => setIsOpen(false)}
            trapFocus={false}
            placement={placement}
            trigger={triggerProps => (
                <Pressable
                    {...triggerProps}
                    nativeID={nativeID}
                    testID={testID}
                    disabled={disabled}
                    onPress={e => {
                        stop(e)
                        setIsOpen(v => !v)
                    }}
                    accessibilityRole='button'
                    accessibilityState={{ disabled, expanded: isOpen }}
                    className={twMerge(
                        'h-8 w-8 items-center justify-center rounded',
                        disabled
                            ? 'pointer-events-none opacity-40'
                            : 'hover:bg-aos-bg-hover-layer active:bg-aos-bg-surface',
                        triggerClassName,
                    )}
                >
                    <MoreVerticalGlyph size={24} />
                </Pressable>
            )}
        >
            <PopoverBackdrop />
            <PopoverContent
                className={twMerge(
                    'min-w-[120px] rounded border border-solid border-border-subtle bg-aos-bg-elevation py-1',
                    contentClassName,
                )}
            >
                <Box>
                    {items.map((item, index) => (
                        <Pressable
                            key={index}
                            onPress={select(item)}
                            disabled={item.disabled}
                            accessibilityRole='menuitem'
                            accessibilityState={{ disabled: item.disabled }}
                            className={twMerge(
                                'px-4 py-2',
                                item.disabled
                                    ? 'pointer-events-none opacity-40'
                                    : 'hover:bg-aos-bg-hover-layer',
                            )}
                        >
                            <Text size='body' tone={item.destructive ? 'critical' : 'default'}>
                                {item.label}
                            </Text>
                        </Pressable>
                    ))}
                </Box>
            </PopoverContent>
        </Popover>
    )
}

MoreMenu.displayName = 'MoreMenu'
